/** @jsxImportSource theme-ui */
import { Box, Checkbox, Label, Text, Textarea } from 'theme-ui';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useApproveCompanyMutation } from '@/store/apiService';

interface Props {
    companyId: string;
    hasScheduledDeletion: boolean;
    onClose: () => void;
    onApproved?: () => void;
}

export const ApproveCompanyModal = ({
    companyId,
    hasScheduledDeletion,
    onClose,
    onApproved,
}: Props) => {
    const [reason, setReason] = useState('');
    const [cancelDeletion, setCancelDeletion] = useState(true);
    const [approvedByAdmin, setApprovedByAdmin] = useState(true);
    const [approveCompany, state] = useApproveCompanyMutation();

    const onConfirm = () => {
        if (!reason.trim()) {
            toast.error('A reason for approving is required', { autoClose: 5000 });
            return;
        }
        approveCompany({
            CompanyId: companyId,
            Reason: reason.trim(),
            CancelScheduledDeletion: hasScheduledDeletion && cancelDeletion,
            SetApprovedByAdmin: approvedByAdmin,
        })
            .unwrap()
            .then((result) => {
                onClose();
                onApproved?.();
                toast.success('Company marked as approved', { autoClose: 5000 });
                (result?.Warnings ?? []).forEach((warning) =>
                    toast.warn(warning, { autoClose: 8000 })
                );
            })
            .catch((err) => {
                toast.error(
                    err?.data?.ResponseStatus?.Message ?? err?.message ?? 'Approving failed',
                    { autoClose: 8000 }
                );
            });
    };

    return (
        <ConfirmationModal
            hideOnClickOutside
            loading={state.isLoading}
            headingText="Mark company as approved"
            content={
                <Box>
                    <Text as="p" mb={2}>
                        The company status will be set to Approved.
                    </Text>
                    <Text as="p" mb={3} color="danger">
                        Licenses that were ended when the company was closed are not restored.
                    </Text>
                    <Label mb={1}>Reason</Label>
                    <Textarea
                        aria-label="Approval reason"
                        rows={3}
                        mb={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                    {hasScheduledDeletion ? (
                        <Label mb={1}>
                            <Checkbox
                                checked={cancelDeletion}
                                onChange={(e) => setCancelDeletion(e.target.checked)}
                            />
                            Cancel scheduled deletion
                        </Label>
                    ) : null}
                    <Label>
                        <Checkbox
                            checked={approvedByAdmin}
                            onChange={(e) => setApprovedByAdmin(e.target.checked)}
                        />
                        Mark as approved by admin
                    </Label>
                </Box>
            }
            onNo={onClose}
            onYes={onConfirm}
        />
    );
};
